

import {
  Dialog,
  DialogBackdrop,
  DialogPanel,
  TransitionChild,
} from "@headlessui/react";
import {
  ArrowRightStartOnRectangleIcon,
  Bars3Icon,
  XMarkIcon,
} from "@heroicons/react/24/outline";
import { useCallback, useEffect, useRef, useState } from "react";
import { LFLogo } from "../../../icons";
import Badge from "../../atoms/Badge/Badge";
import AlertDialog from "../../molecules/Dialog/AlertDialog";

export interface AppShellProps extends React.PropsWithChildren {
  /** Ends the Auth0 session. Supplied by AppLayout. */
  onLogout: () => void;
  /** Sign-out confirmation shown. Optional; used for analytics. */
  onLogoutPrompt?: () => void;
  /** Sign-out confirmation dismissed without signing out. */
  onLogoutCancel?: () => void;
  /** Mobile hamburger menu opened. */
  onMobileMenuOpen?: () => void;
  navSections: JSX.Element[];
  userNickname?: string;
  userPicture?: any;
}

// Sentinel marker pushed into history.state when the mobile sidebar opens so we
// can distinguish a "back pressed while sidebar open" popstate from a real
// browser back navigation. See PAD-138.
const SIDEBAR_HISTORY_STATE_KEY = "__lfSidebarOpen";

export default function AppShell(props: AppShellProps) {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [logoutDialogOpen, setLogoutDialogOpen] = useState(false);

  // Tracks whether we've pushed a sentinel history entry for the currently
  // open sidebar. This ref — not history.state — is the source of truth for
  // "is there a sentinel to pop?" so cleanup doesn't break if navigation
  // replaces the top of the history stack (e.g. nav-item -> router.push)
  // before this effect unwinds.
  const sentinelPushedRef = useRef(false);

  // When the mobile hamburger sidebar is open, the Android hardware back button
  // (and browser back button) should close the sidebar rather than navigating
  // backwards through history. We push a sentinel history entry when the
  // sidebar opens and listen for popstate to close it. PAD-138.
  useEffect(() => {
    if (typeof window === "undefined") return;

    if (!sidebarOpen) return;

    // Push a sentinel entry so the next back press pops this entry instead of
    // navigating away from the current page.
    const sentinelState = {
      ...(window.history.state ?? {}),
      [SIDEBAR_HISTORY_STATE_KEY]: true,
    };
    window.history.pushState(sentinelState, "", window.location.href);
    sentinelPushedRef.current = true;

    const handlePopState = () => {
      // Browser has already popped the sentinel entry; just mirror that in
      // component state.
      sentinelPushedRef.current = false;
      setSidebarOpen(false);
    };

    window.addEventListener("popstate", handlePopState);

    return () => {
      window.removeEventListener("popstate", handlePopState);
      // Defense in depth: if the sentinel wasn't cleaned up via popstate or
      // closeSidebar (e.g. something else set sidebarOpen=false directly),
      // pop it — but only if it's still on top of the stack. If a route
      // transition has already replaced the top entry, popping here would
      // eat that navigation instead of the sentinel, so we skip.
      if (
        sentinelPushedRef.current &&
        window.history.state?.[SIDEBAR_HISTORY_STATE_KEY]
      ) {
        window.history.back();
      }
      sentinelPushedRef.current = false;
    };
  }, [sidebarOpen]);

  // All sidebar-close paths should route through this helper so the sentinel
  // is removed deterministically at click time rather than during useEffect
  // cleanup (which can race with Next.js route transitions).
  const closeSidebar = useCallback(() => {
    if (sentinelPushedRef.current) {
      sentinelPushedRef.current = false;
      // Pops the sentinel; popstate fires asynchronously, and handlePopState
      // runs setSidebarOpen(false). Used for Escape, backdrop, and the X
      // button — none of which trigger a concurrent router.push.
      window.history.back();
    } else {
      setSidebarOpen(false);
    }
  }, []);

  // Nav-link clicks need a different path from closeSidebar's history.back():
  // a popstate firing while the router is mid-navigation races the push. Strip
  // the sentinel marker via replaceState instead — no popstate fires, the
  // navigation proceeds, and the resulting history is [..., page,
  // unmarked-sentinel, newPage], one extra same-URL entry users may step
  // through with browser-back.
  const handleNavClickCapture = useCallback(() => {
    if (sentinelPushedRef.current) {
      sentinelPushedRef.current = false;
      const currentState = window.history.state;
      if (
        typeof currentState === "object" &&
        currentState !== null &&
        SIDEBAR_HISTORY_STATE_KEY in currentState
      ) {
        const { [SIDEBAR_HISTORY_STATE_KEY]: _unused, ...rest } = currentState;
        window.history.replaceState(rest, "", window.location.href);
      }
    }
    setSidebarOpen(false);
  }, []);

  const handleLogout = () => {
    props.onLogout();
  };

  const openLogoutDialog = () => {
    setLogoutDialogOpen(true);
    props.onLogoutPrompt?.();
  };

  return (
    <>
      {/*
        This example requires updating your template:

        ```
        <html class="h-full bg-white">
        <body class="h-full">
        ```
      */}
      <div className={`max-w-screen-2xl mx-auto`}>
        <Dialog
          open={sidebarOpen}
          onClose={closeSidebar}
          className="relative z-30 lg:hidden"
        >
          <DialogBackdrop
            transition
            className="fixed inset-0 bg-gray-900/80 transition-opacity duration-300 ease-linear data-[closed]:opacity-0"
          />

          <div className="fixed inset-0 flex">
            <DialogPanel
              transition
              className="relative mr-16 flex w-full max-w-xs flex-1 transform transition duration-300 ease-in-out data-[closed]:-translate-x-full"
            >
              <TransitionChild>
                <div className="absolute left-full top-0 flex w-16 justify-center pt-5 duration-300 ease-in-out data-[closed]:opacity-0">
                  <button
                    type="button"
                    onClick={closeSidebar}
                    className="-m-2.5 p-2.5"
                  >
                    <span className="sr-only">Close sidebar</span>
                    <XMarkIcon
                      aria-hidden="true"
                      className="h-6 w-6 text-white"
                    />
                  </button>
                </div>
              </TransitionChild>
              {/* Sidebar component, swap this element with another sidebar if you like */}
              <div className="flex grow flex-col gap-y-5 overflow-y-auto bg-lf-blue-light px-6 pb-2">
                <div className="flex h-16 shrink-0 items-center ml-4">
                  <img
                    alt="Liberty Fund"
                    src={LFLogo.src}
                    className="h-12 w-auto img-white"
                  />
                </div>
                <nav className="flex flex-1 flex-col">
                  <ul
                    role="list"
                    className="flex flex-1 flex-col gap-y-2"
                    // Capture phase so the sentinel is cleared before
                    // Link's onClick calls router.push. See
                    // handleNavClickCapture.
                    onClickCapture={handleNavClickCapture}
                  >
                    {props.navSections}
                  </ul>
                </nav>
              </div>
            </DialogPanel>
          </div>
        </Dialog>

        {/* Static sidebar for desktop */}
        <div className="hidden print:hidden lg:fixed lg:inset-y-0 lg:z-30 lg:flex lg:w-72 lg:flex-col">
          {/* Sidebar component, swap this element with another sidebar if you like */}
          <div className="flex grow flex-col gap-y-5 overflow-y-auto bg-lf-blue-light px-6">
            <div className="flex h-24 ml-4 items-center">
              <img
                alt="Liberty Fund"
                src={LFLogo.src}
                className="h-16 w-auto img-white"
              />
            </div>
            <nav className="flex flex-1 flex-col">
              <ul role="list" className="flex flex-1 flex-col gap-y-7">
                <li>
                  <ul role="list" className="-mx-2 space-y-1">
                    {props.navSections}
                  </ul>
                </li>
                <li
                  className="-mx-6 mt-auto border-t border-t-lf-blue-dark cursor-pointer"
                  onClick={openLogoutDialog}
                >
                  <span
                    className="flex items-center gap-x-4 px-6 py-3 text-sm font-semibold leading-6 text-gray-200 hover:bg-lf-blue-dark"
                    data-testid={"host-shell-signout-panel"}
                  >
                    <img
                      alt=""
                      src={props.userPicture}
                      className="h-8 w-8 rounded-full bg-lf-blue-dark"
                    />
                    <span className="sr-only">
                      Your user is {props.userNickname}
                    </span>
                    <span aria-hidden="true">{props.userNickname}</span>
                    <div className="flex-1" />
                    <ArrowRightStartOnRectangleIcon
                      aria-hidden="true"
                      className="flex h-6 w-6 text-gray-200"
                    />
                  </span>
                </li>
              </ul>
            </nav>
          </div>
        </div>

        <div className="sticky top-0 z-30 flex items-center gap-x-6 bg-lf-blue-light px-4 py-2 shadow-sm sm:px-6 lg:hidden print:hidden">
          <button
            type="button"
            onClick={() => {
              setSidebarOpen(true);
              props.onMobileMenuOpen?.();
            }}
            className="-m-2.5 p-2.5 text-gray-200 lg:hidden"
          >
            <span className="sr-only">Open sidebar</span>
            <Bars3Icon aria-hidden="true" className="h-6 w-6" />
          </button>
          <div className="flex-1 text-sm font-semibold leading-6 text-white">
            Liberty Fund Portal
          </div>
          <div className="flex items-center m-0 p-0" onClick={openLogoutDialog}>
            <img
              alt=""
              src={props.userPicture}
              className="h-10 w-10 rounded-full z-[1]"
            />
            <Badge className="overflow-clip aspect-square -ml-3 px-1 py-1 rounded-l-lg scale-75">
              &nbsp;
              <ArrowRightStartOnRectangleIcon className="h-5 w-5 text-white" />
            </Badge>
          </div>
        </div>

        <div className="flex flex-col lg:pl-72 overscroll-x-none print:block">
          {props.children}
        </div>
        <AlertDialog
          testId={"host-shell-signout-dialog"}
          title={"Sign Out?"}
          confirmButtonTitle="Sign Out"
          content={
            "Please confirm you want to sign out and return to the login screen."
          }
          open={logoutDialogOpen}
          customIcon={ArrowRightStartOnRectangleIcon}
          onClose={(value) => {
            if (value) {
              try {
                // Clear persisted redux state so UI doesn't momentarily show stale user info
                localStorage.removeItem("persist:root");
              } catch {}

              handleLogout();
            } else {
              props.onLogoutCancel?.();
            }
            setLogoutDialogOpen(false);
          }}
        />
      </div>
    </>
  );
}
